'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '@/lib/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (credentials: { email?: string; phone?: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount (run only once)
  useEffect(() => {
    let active = true;

    const isAuthError = (err: unknown) =>
      err instanceof Error && (
        err.message.includes('Session expired') ||
        err.message.includes('Unauthorized') ||
        err.message.includes('401')
      );

    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (active) setLoading(false);
        return;
      }

      if (active) setLoading(true);
      // Re-set cookie in case it expired but localStorage still has token
      document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`; // 7 days

      // Retry transient network failures so we don't end up "tokened but user=null"
      // (which leaves the app routed-in but treating the user as unauthenticated).
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts && active; attempt++) {
        try {
          const res = await authApi.getUser();
          if (!active) return;
          setUser(res.data || res); // handles { data: user } and direct user object
          break;
        } catch (err) {
          if (isAuthError(err)) {
            localStorage.removeItem('token');
            document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
            break;
          }
          // Network error — keep the token and retry with backoff.
          console.warn(`Network error during auth check (attempt ${attempt}/${maxAttempts}).`);
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 1000 * attempt));
          }
        }
      }

      if (active) setLoading(false);
    };

    // Add a small delay to prevent flash of loading state
    const timer = setTimeout(() => {
      initAuth();
    }, 100);

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials: { email?: string; phone?: string; password: string }) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authApi.login(credentials);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      // Also set token in cookie for middleware access
      document.cookie = `token=${token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`; // 7 days
      setUser(userData);
      // Let other contexts (e.g. SettingsContext) refetch authenticated data now
      // that a token is available.
      window.dispatchEvent(new Event('auth:login'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      // Clear the token cookie
      document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, error, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
